import { useEffect, useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuthenticatedApi } from "../../auth";
import { UserContext } from "../../auth.capabilities";
import ProgressSpinnerComponent from "./ProgressSpinner";
import AccessDeniedComponent from "./AccessDenied";

const UserCapabilitiesProvider = ({ children }) => {
  const defaultCapabilities = {
    hasAdminRights: false,
    hasInternalUserRights: false,
    hasExternalUserRights: false,
  };

  const { instance, accounts } = useMsal();
  const [user, setUser] = useState(defaultCapabilities);
  const [accessControl, setAccessControl] = useState({
    allowed: false,
    message: null,
  });
  const [loading, setLoading] = useState(true);
  const tokenRequested = useRef(false);

  const { callProtectedApi, apiBaseUrl } = useAuthenticatedApi();

  useEffect(() => {
    const loadUser = async () => {
      if (accounts.length === 0) return;

      try {
        // Switched from /api/v1/auth/me to /api/v2/auth/me — V2 keys identity on the
        // strict (Entra oid + email) pair via UserMigration, so the same person
        // signing in with their AD-synced email vs Entra-native email no longer
        // ends up as two tblUser rows. V1 behaviour is left intact server-side
        // (still mounted at /api/v1/auth/me) until this change is proven.
        const res = await callProtectedApi(`${apiBaseUrl}/api/v2/auth/me`);
        const me = await res.data;

        if (!me?.success) {
          throw new Error(`/api/v2/auth/me endpoint failed: ${res.status}`);
        }

        const { capabilities, role, upn, user } = me;

        setUser({
          ...(capabilities || defaultCapabilities), // dynamic rights
          role,
          upn,
          user,
        });

        setAccessControl({
          allowed: true,
          message: null,
        });
      } catch (err) {
        setLoading(false);

        // console.log("Error type:", err.errorCode);
        // console.log("Full error:", err);

        const status = err?.response?.status;
        const errorMessage = err?.response?.data?.message;

        if (status === 403 || status === 401) {
          setAccessControl({
            allowed: false,
            message: errorMessage,
          });
          return;
        }

        setAccessControl({
          allowed: false,
          message: "We were unable to load your user information.",
        });
      }

      setLoading(false);
    };

    if (!tokenRequested.current) {
      tokenRequested.current = true;
      loadUser();
    }
  }, [accounts, instance]);

  if (loading) {
    return <ProgressSpinnerComponent />;
  }

  if (!accessControl?.allowed) {
    return <AccessDeniedComponent message={accessControl?.message} />;
  }

  // console.log("USER", user)

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};

export default UserCapabilitiesProvider;
